import { toItemCollections } from '@toolproof-core/visualization/derivation/_pure/derivation-values';
import type { ItemCollection } from '@toolproof-core/visualization/_contract/collection';
import type {
    ItemBehaviorController,
    ItemParametricState,
} from '@toolproof-core/visualization/_contract/item';
import type {
    ItemCollectionDerivation,
} from '@toolproof-core/visualization/derivation/_contract/derivation';

import {
    buildToolStepItemKey,
    deriveSharedActivationLinks,
} from '@/workflow/_pure/derivation/activationLinks';
import {
    deriveBaseConstellationDerivations,
    deriveBaseSpaceRenderOptions,
    deriveResourceCollections as deriveBaseResourceCollections,
    deriveSyntheticItemCollectionDerivations as deriveBaseSyntheticItemCollectionDerivations,
    enrichBaseConstellationDerivations,
} from '@/workflow/_pure/derivation/baseDerivation';
import { deriveCaseConnectorSeeds } from '@/workflow/_pure/derivation/caseMembership';
import {
    buildCaseConstellationDerivations,
    buildMacroStepConnectorConstellationDerivations,
    buildWorkflowStateEntryConnectorConstellationDerivations,
} from '@/workflow/_pure/derivation/constellations';
import { toItemParametricState } from '@/workflow/_pure/derivation/itemParametricState';
import {
    deriveMacroStepConnectorCollectionDerivationsFromSeeds,
} from '@/workflow/_pure/derivation/macroStepConnectors';
import { deriveOverviewTransition } from '@/workflow/_pure/derivation/overviewNavigation';
import { deriveToolStepOuterRimDerivation } from '@/workflow/_pure/derivation/toolStepOuterRim';
import {
    WORKFLOW_KEYS,
    buildToolItemKey,
    buildToolStepCollectionKey,
    buildToolStepConstellationKey,
} from '@/workflow/_pure/keyCodec';
import { requireBuilderRuntimeState } from '@/workflow/modes/_@shared/_pure/runtimeStateGuards';
import { requireConfig } from '@/workflow-builder/_pure/config/config';
import { deriveCaseItemCollectionDerivations } from '@/workflow-builder/_pure/derivation/caseBands';
import { deriveCaseMembership } from '@/workflow-builder/_pure/derivation/caseMembership';
import {
    deriveCommittedToolStepConstellationDerivation,
    deriveCommittedToolStepItemCollectionDerivation,
    deriveCommittedWorkflowStateEntryCollections,
    deriveCommittedWorkflowStateEntryConnectorCollections,
    deriveToolStepRingLayout,
} from '@/workflow-builder/_pure/derivation/committedProjection';
import { deriveMacroStepConnectorSeeds } from '@/workflow-builder/_pure/derivation/macroStepConnectors';
import { deriveProjectionRuntimeState } from '@/workflow-builder/_pure/derivation/projectionRuntimeState';
import {
    deriveSelectedInputWorkflowStateEntryActiveRef,
    deriveSelectedToolItemKey,
} from '@/workflow-builder/_pure/interaction/modeAugmentationHelpers';
import { createActionContext } from '@/workflow-builder/_pure/model/context';
import { deriveItemBehavior } from '@/workflow-builder/interaction/itemBehaviorRouting';
import type {
    ModeAugmentation,
} from '@/workflow/modes/_@shared/_contract/mode';

export const MODE_AUGMENTATION_CONTROLLER: ModeAugmentation = {
    deriveResourceCollections(args) {
        return deriveBaseResourceCollections({
            cosmosData: args.input.cosmosData,
            config: args.config,
        });
    },

    deriveSyntheticItemCollectionDerivations(args) {
        const builderRuntimeState = requireBuilderRuntimeState(args.input.runtimeState);
        const workflowKeyScope = {
            workflowHandle: builderRuntimeState.mutableWorkflow.handle,
            threadIndex: 0,
        };
        const toolStepCollectionKey = buildToolStepCollectionKey(workflowKeyScope);
        const baseSyntheticItemCollectionDerivations = deriveBaseSyntheticItemCollectionDerivations({
            cosmosData: args.input.cosmosData,
            config: args.config,
        });
        const projectionRuntimeState = deriveProjectionRuntimeState(builderRuntimeState);
        const includeAllInputRolesByStepKeySet = builderRuntimeState.selectionState.selectedToolStep
            && (builderRuntimeState.phase === 'binding_inputs' || builderRuntimeState.phase === 'reviewing_input_bindings')
            ? new Set([builderRuntimeState.selectionState.selectedToolStep.stepKey])
            : undefined;

        const committedToolStepCollectionDerivation = deriveCommittedToolStepItemCollectionDerivation({
            itemCollectionKey: toolStepCollectionKey,
            runtimeState: projectionRuntimeState,
            config: args.config,
        });
        const builderToolStepLayout = deriveToolStepRingLayout({
            cosmosData: args.input.cosmosData,
            projectedToolSteps: projectionRuntimeState.committedToolSteps,
            config: args.config,
        });
        const toolStepOuterRimDerivation = deriveToolStepOuterRimDerivation({
            ringRadii: builderToolStepLayout.ringRadii,
            ringGap: builderToolStepLayout.ringGap,
            scope: workflowKeyScope,
            config: args.config,
        });

        const toolStepCollection = committedToolStepCollectionDerivation === undefined
            ? ({
                itemCollectionKind: 'entity',
                itemCollectionKey: toolStepCollectionKey,
                items: [],
            } as ItemCollection<'entity'>)
            : toItemCollections([committedToolStepCollectionDerivation])[0] as ItemCollection<'entity'>;

        const caseProjection = deriveCaseItemCollectionDerivations({
            cosmosData: args.input.cosmosData,
            runtimeState: builderRuntimeState,
            projectedToolSteps: projectionRuntimeState.committedToolSteps,
            config: args.config,
            workflowKeyScope,
        });

        const membershipArgs = {
            runtimeState: builderRuntimeState,
            projectedToolSteps: projectionRuntimeState.committedToolSteps,
        };
        const caseMemberships = deriveCaseMembership(membershipArgs);
        const macroStepConnectorSeeds = [
            ...deriveMacroStepConnectorSeeds(membershipArgs),
            ...(args.config.domainPresentation.connectors.macroStep.casePair?.enabled
                ? deriveCaseConnectorSeeds(caseMemberships)
                : []),
        ];

        const preMacroStepItemCollectionDerivations: ItemCollectionDerivation[] = [
            ...baseSyntheticItemCollectionDerivations,
            ...(committedToolStepCollectionDerivation ? [committedToolStepCollectionDerivation] : []),
            ...(toolStepOuterRimDerivation === undefined
                ? []
                : [toolStepOuterRimDerivation.itemCollectionDerivation]),
            ...caseProjection.itemCollectionDerivations,
        ];
        const contextItemCollectionDerivations: ItemCollectionDerivation[] = [
            ...preMacroStepItemCollectionDerivations,
        ];
        const contextItemCollections = [
            ...args.itemCollections,
            ...toItemCollections(contextItemCollectionDerivations),
        ];

        const macroStepConnectorCollectionDerivations = deriveMacroStepConnectorCollectionDerivationsFromSeeds({
            itemCollections: contextItemCollections,
            config: args.config,
            seeds: macroStepConnectorSeeds,
            workflowKeyScope,
        });

        const workflowStateEntryProjection = deriveCommittedWorkflowStateEntryCollections({
            cosmosData: args.input.cosmosData,
            config: args.config,
            builderRuntimeState,
            runtimeState: projectionRuntimeState,
            itemCollections: contextItemCollections,
            toolStepCollection,
            includeAllInputRolesByStepKeySet,
            workflowKeyScope,
        });

        const connectorProjection = deriveCommittedWorkflowStateEntryConnectorCollections({
            cosmosData: args.input.cosmosData,
            config: args.config,
            runtimeState: projectionRuntimeState,
            itemCollections: [
                ...contextItemCollections,
                ...toItemCollections([
                    ...workflowStateEntryProjection.itemCollectionDerivations,
                    ...macroStepConnectorCollectionDerivations,
                ]),
            ],
            workflowKeyScope,
        });

        return [
            ...contextItemCollectionDerivations,
            ...workflowStateEntryProjection.itemCollectionDerivations,
            ...macroStepConnectorCollectionDerivations,
            ...connectorProjection.itemCollectionDerivations,
        ];
    },

    deriveConstellationDerivations(args) {
        const builderRuntimeState = requireBuilderRuntimeState(args.input.runtimeState);
        const workflowKeyScope = {
            workflowHandle: builderRuntimeState.mutableWorkflow.handle,
            threadIndex: 0,
        };
        const toolStepCollectionKey = buildToolStepCollectionKey(workflowKeyScope);
        const toolStepConstellationKey = buildToolStepConstellationKey(workflowKeyScope);
        const baseConstellationDerivations = deriveBaseConstellationDerivations({
            itemCollections: args.itemCollections,
            config: args.config,
        });
        const projectionRuntimeState = deriveProjectionRuntimeState(builderRuntimeState);
        const includeAllInputRolesByStepKeySet = builderRuntimeState.selectionState.selectedToolStep
            && (builderRuntimeState.phase === 'binding_inputs' || builderRuntimeState.phase === 'reviewing_input_bindings')
            ? new Set([builderRuntimeState.selectionState.selectedToolStep.stepKey])
            : undefined;

        const committedToolStepCollection = args.itemCollections.find(
            (collection): collection is ItemCollection<'entity'> =>
                collection.itemCollectionKind === 'entity'
                && collection.itemCollectionKey === toolStepCollectionKey
        );

        const toolStepCollection = committedToolStepCollection ?? ({
            itemCollectionKind: 'entity',
            itemCollectionKey: toolStepCollectionKey,
            items: [],
        } as ItemCollection<'entity'>);

        const toolStepConstellationDerivation = committedToolStepCollection && committedToolStepCollection.items.length > 0
            ? deriveCommittedToolStepConstellationDerivation({
                runtimeState: builderRuntimeState,
                constellationKey: toolStepConstellationKey,
                toolStepCollection: committedToolStepCollection,
                cosmosData: args.input.cosmosData,
                projectedToolSteps: projectionRuntimeState.committedToolSteps,
                config: args.config,
            })
            : undefined;
        const builderToolStepLayout = deriveToolStepRingLayout({
            cosmosData: args.input.cosmosData,
            projectedToolSteps: projectionRuntimeState.committedToolSteps,
            config: args.config,
        });
        const toolStepOuterRimDerivation = deriveToolStepOuterRimDerivation({
            ringRadii: builderToolStepLayout.ringRadii,
            ringGap: builderToolStepLayout.ringGap,
            scope: workflowKeyScope,
            config: args.config,
        });

        const workflowStateEntryProjection = deriveCommittedWorkflowStateEntryCollections({
            cosmosData: args.input.cosmosData,
            config: args.config,
            builderRuntimeState,
            runtimeState: projectionRuntimeState,
            itemCollections: args.itemCollections,
            toolStepCollection,
            includeAllInputRolesByStepKeySet,
            workflowKeyScope,
        });

        const caseProjection = deriveCaseItemCollectionDerivations({
            cosmosData: args.input.cosmosData,
            runtimeState: builderRuntimeState,
            projectedToolSteps: projectionRuntimeState.committedToolSteps,
            config: args.config,
            workflowKeyScope,
        });

        const caseConstellationDerivations = buildCaseConstellationDerivations({
            caseItemCollectionDerivations: caseProjection.itemCollectionDerivations,
            caseMetadataByItemKey: caseProjection.metadataByItemKey,
        });

        const macroStepConnectorConstellationDerivations = buildMacroStepConnectorConstellationDerivations({
            itemCollections: args.itemCollections,
        });

        const workflowStateEntryConnectorConstellationDerivations = buildWorkflowStateEntryConnectorConstellationDerivations({
            itemCollections: args.itemCollections,
        });

        return enrichBaseConstellationDerivations({
            constellationDerivations: [
                ...baseConstellationDerivations,
                ...(toolStepConstellationDerivation ? [toolStepConstellationDerivation] : []),
                ...(toolStepOuterRimDerivation === undefined
                    ? []
                    : [toolStepOuterRimDerivation.constellationDerivation]),
                ...workflowStateEntryProjection.constellationDerivations,
                ...caseConstellationDerivations,
                ...macroStepConnectorConstellationDerivations,
                ...workflowStateEntryConnectorConstellationDerivations,
            ],
            config: args.config,
        });
    },

    deriveCameraInitializationKey(args) {
        // The workflow handle survives edits, so the camera fits once per opened workflow.
        return `workflow-builder:${requireBuilderRuntimeState(args.input.runtimeState).mutableWorkflow.handle}`;
    },

    deriveSpaceRenderOptions(args) {
        const builderRuntimeState = requireBuilderRuntimeState(args.input.runtimeState);
        const workflowKeyScope = {
            workflowHandle: builderRuntimeState.mutableWorkflow.handle,
            threadIndex: 0,
        };
        const toolStepConstellationKey = buildToolStepConstellationKey(workflowKeyScope);
        const baseSpaceRenderOptions = deriveBaseSpaceRenderOptions({
            config: args.config,
        });
        const projectionRuntimeState = deriveProjectionRuntimeState(builderRuntimeState);
        const selectedToolItemKey = deriveSelectedToolItemKey(builderRuntimeState);
        const activeDraftToolStep = builderRuntimeState.selectionState.selectedToolStep
            && (builderRuntimeState.phase === 'binding_inputs' || builderRuntimeState.phase === 'reviewing_input_bindings')
            ? builderRuntimeState.selectionState.selectedToolStep
            : undefined;
        const selectedDraftInputRoleName = activeDraftToolStep
            ? builderRuntimeState.selectionState.selectedInputRoleName ?? null
            : null;
        const activationLinks = deriveSharedActivationLinks({
            itemCollections: args.itemCollections,
            toolStepConstellationKey,
            orderedToolSteps: projectionRuntimeState.committedToolSteps.map((toolStep) => ({
                stepKey: toolStep.stepKey,
                toolHandle: toolStep.toolHandle,
            })),
            activeToolStep: activeDraftToolStep
                ? {
                    stepKey: activeDraftToolStep.stepKey,
                    toolHandle: activeDraftToolStep.toolHandle,
                }
                : undefined,
            workflowKeyScope,
            caseMemberships: deriveCaseMembership({
                runtimeState: builderRuntimeState,
                projectedToolSteps: projectionRuntimeState.committedToolSteps,
            }),
        });
        const selectedInputWorkflowStateEntryRef = activeDraftToolStep && selectedDraftInputRoleName
            ? deriveSelectedInputWorkflowStateEntryActiveRef({
                itemCollections: args.itemCollections,
                selectedStepKey: activeDraftToolStep.stepKey,
                selectedRoleName: selectedDraftInputRoleName,
            })
            : null;
        const activeItemKeyByConstellationKey = activeDraftToolStep
            ? {
                [toolStepConstellationKey]: buildToolStepItemKey(activeDraftToolStep.stepKey),
                ...(selectedInputWorkflowStateEntryRef === null
                    ? {}
                    : { [selectedInputWorkflowStateEntryRef.constellationKey]: selectedInputWorkflowStateEntryRef.itemKey }),
            }
            : (selectedToolItemKey === undefined
                ? undefined
                : {
                    [WORKFLOW_KEYS.constellations.tool]: selectedToolItemKey,
                });

        return {
            ...baseSpaceRenderOptions,
            ...(activeItemKeyByConstellationKey === undefined && activationLinks.length === 0
                ? {}
                : {
                    activation: {
                        ...(activeItemKeyByConstellationKey === undefined
                            ? {}
                            : { activeItemKeyByConstellationKey }),
                        ...(activationLinks.length > 0 ? { activationLinks } : {}),
                    },
                }),
            builderRuntimeState,
        };
    },

    deriveItemParametricState(args): ItemParametricState | undefined {
        const builderRuntimeState = requireBuilderRuntimeState(args.input.runtimeState);
        const actionContext = createActionContext({
            config: args.config,
            cosmosData: args.input.cosmosData,
            runtimeState: builderRuntimeState,
        });
        const builderConfig = requireConfig(args.config).adapterModeConfig;
        const dimOpacity = builderConfig.nonActiveDimming.opacityByConstellationKey?.[WORKFLOW_KEYS.constellations.tool]
            ?? builderConfig.nonActiveDimming.opacityByLayoutKind?.['sector-ring']
            ?? builderConfig.nonActiveDimming.defaultOpacity
            ?? 0.35;
        const ineligibleToolRenderState = actionContext.isLoopWhatSlotSelection
            ? {
                itemParametricStateByKey: Object.fromEntries(
                    Array.from(actionContext.ineligibleSelectableToolHandleSet).map((toolHandle) => [
                        buildToolItemKey(toolHandle),
                        {
                            visualStyle: {
                                opacity: dimOpacity,
                            },
                            visibility: {
                                visible: dimOpacity > 0,
                            },
                        },
                    ]),
                ),
            }
            : undefined;

        return toItemParametricState(ineligibleToolRenderState);
    },

    deriveItemBehaviorController(args): ItemBehaviorController | undefined {
        return deriveItemBehavior({
            runtimeState: requireBuilderRuntimeState(args.input.runtimeState),
            cosmosData: args.input.cosmosData,
            config: args.config,
            baseItemBehavior: undefined,
            allowRenderedToolStepClicksForRebinding: true,
        });
    },
    deriveTransitionPhases(args) {
        return deriveOverviewTransition(requireBuilderRuntimeState(args.input.runtimeState));
    },
};
